/**
 * イージング関数 (ease-out cubic)
 * @param {number} t - 0から1の進行度
 * @returns {number} イージング適用後の値
 */
function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * 内側SVGのユーザ座標をコンテンツ座標（外側SVG座標）に変換する。
 */
function svgToContentCoords(svgRoot, svgX, svgY) {
  const outerSvg = svgRoot.ownerSVGElement;
  if (!outerSvg) return { x: svgX, y: svgY };

  const vb = svgRoot.viewBox?.baseVal;
  if (!vb || vb.width === 0 || vb.height === 0) return { x: svgX, y: svgY };

  const innerW =
    svgRoot.width?.baseVal?.value ||
    outerSvg.width?.baseVal?.value ||
    outerSvg.clientWidth;
  const innerH =
    svgRoot.height?.baseVal?.value ||
    outerSvg.height?.baseVal?.value ||
    outerSvg.clientHeight;

  // xMidYMid meet
  const sx = innerW / vb.width;
  const sy = innerH / vb.height;
  const s = Math.min(sx, sy);

  const tx = (innerW - vb.width * s) / 2;
  const ty = (innerH - vb.height * s) / 2;

  return {
    x: (svgX - vb.x) * s + tx,
    y: (svgY - vb.y) * s + ty,
  };
}

/**
 * SVG座標を指定して画面中央に滑らかにアニメーション移動
 * @param {Object} Viewer - react-svg-pan-zoom の Viewer インスタンス
 * @param {SVGSVGElement} svgRoot - SVG のルート要素
 * @param {number} svgX - SVG座標系でのX座標
 * @param {number} svgY - SVG座標系でのY座標
 * @param {number} zoomLevel - ズームレベル
 * @param {number} duration - アニメーション時間（ミリ秒）
 */
export function focusOnPointAnimated(
  Viewer,
  svgRoot,
  svgX,
  svgY,
  zoomLevel = 1.0,
  duration = 800,
) {
  if (!Viewer || !svgRoot || svgX == null || svgY == null) return;

  // SVG座標をコンテンツ座標に変換
  const contentPt = svgToContentCoords(svgRoot, svgX, svgY);
  const targetViewerX = contentPt.x;
  const targetViewerY = contentPt.y;

  // 現在のビューアーの中心座標を計算
  const currentValue = Viewer.getValue();
  const viewerWidth = currentValue.viewerWidth;
  const viewerHeight = currentValue.viewerHeight;
  const currentCenterX = (viewerWidth / 2 - currentValue.e) / currentValue.a;
  const currentCenterY = (viewerHeight / 2 - currentValue.f) / currentValue.a;
  const currentZoom = currentValue.a;

  const startZoom = currentZoom;
  const startTime = performance.now();

  function animate(currentTime) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easeOutCubic(progress);

    const x = currentCenterX + (targetViewerX - currentCenterX) * easedProgress;
    const y = currentCenterY + (targetViewerY - currentCenterY) * easedProgress;
    const zoom = startZoom + (zoomLevel - startZoom) * easedProgress;

    Viewer.setPointOnViewerCenter(x, y, zoom);

    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  }

  requestAnimationFrame(animate);
}

/**
 * 画面中央を基準に滑らかにズーム
 * @param {Object} Viewer - react-svg-pan-zoom の Viewer インスタンス
 * @param {number} scaleFactor - ズーム倍率（1.2でズームイン、0.8でズームアウト）
 * @param {number} duration - アニメーション時間（ミリ秒）
 */
export function zoomOnViewerCenterAnimated(
  Viewer,
  scaleFactor,
  duration = 200,
) {
  if (!Viewer) return;

  const currentValue = Viewer.getValue();
  const currentZoom = currentValue.a;
  const targetZoom = currentZoom * scaleFactor;

  // 現在のビューアーの中心座標を計算
  const viewerWidth = currentValue.viewerWidth;
  const viewerHeight = currentValue.viewerHeight;
  const centerX = (viewerWidth / 2 - currentValue.e) / currentValue.a;
  const centerY = (viewerHeight / 2 - currentValue.f) / currentValue.a;

  const startZoom = currentZoom;
  const startTime = performance.now();

  function animate(currentTime) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easeOutCubic(progress);

    // ズームを補間
    const zoom = startZoom + (targetZoom - startZoom) * easedProgress;

    Viewer.setPointOnViewerCenter(centerX, centerY, zoom);

    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  }

  requestAnimationFrame(animate);
}
